import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NoImg from "../assets/images/no-image-icon-23485.png";
import { MapPinIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from "react-markdown";
import starGreen from "../assets/icons/StarGreen.svg";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import useUserStore from "../store/userStore";
import { useFavorite } from "../hooks/useFavorites";
import { getCachedPlaceImageUrls } from "../services/placeImageCache";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface PlaceCardProps {
  place: {
    id: string;
    name: string;
    rate: number;
    image_urls?: string[];
    address?: string;
    city?: string;
    opening_hours?: string;
    description?: string;
    review_count: number;
  };
}

const ImageWithBlurPreview: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative w-full h-48 overflow-hidden rounded-t-lg">
      {/* Blurred version of the same image — loads fast, shown while full res loads */}
      <img
        src={src}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl brightness-90"
      />
      {/* Full-res image fades in on top */}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = NoImg;
          setLoaded(true);
        }}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:brightness-75 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

const PlaceCard: React.FC<PlaceCardProps> = ({ place }) => {
  const suppliedImages = useMemo(() => place.image_urls?.filter(Boolean) ?? [], [place.image_urls]);
  const [images, setImages] = useState<string[]>(suppliedImages);
  const [imagesLoading, setImagesLoading] = useState(suppliedImages.length === 0);
  const { user } = useUserStore();
  const {
    isFavorite,
    isLoading: isFavoriteLoading,
    toggleFavorite,
  } = useFavorite(user?.userId, "place", place.id);

  useEffect(() => {
    if (suppliedImages.length > 0) {
      setImages(suppliedImages);
      setImagesLoading(false);
      return;
    }

    let isMounted = true;
    const loadImages = async () => {
      const urls = await getCachedPlaceImageUrls(place.id);
      if (isMounted) {
        setImages(urls);
        setImagesLoading(false);
      }
    };
    loadImages();
    return () => { isMounted = false; };
  }, [place.id, suppliedImages]);

  const getImageUrl = (fileUrl?: string) => {
    if (!fileUrl) return NoImg;
    if (fileUrl.startsWith("http")) return fileUrl;
    return `${BASE_URL}${fileUrl}`;
  };

  const firstImage = images[0];

  const handleToggleFavorite = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.userId) {
      toast.error("لطفاً ابتدا وارد حساب کاربری خود شوید", {
        className: "toast",
        hideProgressBar: true,
      });
      return;
    }

    if (isFavoriteLoading) return;

    try {
      const result = await toggleFavorite();
      toast.success(
        result.action === "added"
          ? `${place.name} به علاقه‌مندی‌ها اضافه شد`
          : `${place.name} از علاقه‌مندی‌ها حذف شد`,
        {
          className: "toast",
          hideProgressBar: true,
        }
      );
    } catch (favoriteError) {
      console.error("Error toggling favorite:", favoriteError);
      toast.error("تغییر علاقه‌مندی با خطا مواجه شد", {
        className: "toast",
        hideProgressBar: true,
      });
    }
  };

  return (
    <article
      className="group relative rounded-lg overflow-hidden bg-white border border-gray hover:shadow-md transition w-64 flex-shrink-0"
    >
      <button
        type="button"
        onClick={handleToggleFavorite}
        disabled={Boolean(user?.userId) && isFavoriteLoading}
        aria-label={isFavorite ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
        aria-pressed={isFavorite}
        title={isFavorite ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
        className={`absolute left-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${
          isFavorite ? "text-[#E5484D]" : "text-black"
        }`}
      >
        {Boolean(user?.userId) && isFavoriteLoading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Heart size={18} fill={isFavorite ? "currentColor" : "none"} />
        )}
      </button>

      <Link to={`/places/${place.id}`} className="block h-full">
        {/* Image with blur preview */}
        {imagesLoading ? (
          <div className="w-full h-48 bg-gray-200 animate-pulse rounded-t-lg" />
        ) : (
          <ImageWithBlurPreview src={getImageUrl(firstImage)} alt={place.name} />
        )}

        {/* Content */}
        <div className="p-3 text-black font-myYekanFaNumRegular">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-myYekanDemibold line-clamp-1 font-myYekanFaNumDemiBold">
              {place.name}
            </h3>
            <div className="flex justify-between items-center gap-2">
              {place.rate > 0 && (
                <div className="flex items-center gap-1 text-[#038545] text-sm">
                  {parseFloat(place.rate.toFixed(1))}
                  <img src={starGreen} className="w-3 h-3 mb-1" alt="rating" />
                </div>
              )}
            </div>
          </div>
          {place.city && (
            <div className="flex items-center justify-between gap-2 text-[11px] mt-2 text-black">
              <div className="flex min-w-0 items-center">
                <MapPinIcon className="h-3 w-3 ml-1 flex-shrink-0" />
                <span className="line-clamp-1">{place.city}</span>
              </div>
              {place.review_count > 0 && (
                <span className="shrink-0 text-gray-500">({place.review_count} نظر)</span>
              )}
            </div>
          )}
          {place.description && (
            <div className="text-[10px] text-black mt-2 line-clamp-2 text-justify font-myYekanFaNumRegular">
              <ReactMarkdown>{place.description}</ReactMarkdown>
            </div>
          )}
        </div>
      </Link>
    </article>
  );
};

export default PlaceCard;
